import React, { useState } from 'react';
import { LogIn, ShieldCheck } from 'lucide-react';

interface LoginViewProps {
  onLogin: (email: string, password?: string) => void;
  loginError: string | null;
}

const LoginView: React.FC<LoginViewProps> = ({ onLogin, loginError }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      onLogin(email.trim(), password);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="glass-card w-full max-w-md p-8 rounded-2xl border border-gray-700 shadow-2xl relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-teal-500 to-blue-600"></div>
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-blue-600/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-teal-600/20 rounded-full blur-3xl"></div>

        <div className="relative z-10 text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-teal-600 to-blue-700 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <ShieldCheck className="text-white" size={32} />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2 font-mono">BORDERLOGIX</h1>
          <p className="text-gray-400 font-mono tracking-wide text-sm">cleared.compliant.reconciled</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your authorized email"
              className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
              Password <span className="text-gray-500 font-normal text-xs ml-1">(Required for Admin)</span>
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
            />
          </div>

          {loginError && (
            <div className="p-3 bg-red-900/50 border border-red-800 rounded-lg text-red-200 text-sm animate-pulse">
              {loginError}
            </div>
          )}

          <button
            type="submit"
            className="w-full bg-gradient-to-r from-teal-600 to-blue-700 hover:from-teal-500 hover:to-blue-600 text-white font-bold py-3 px-4 rounded-lg shadow-lg transform transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
          >
            <LogIn size={20} />
            Access Platform
          </button>
        </form>

        <div className="mt-8 text-center relative z-10">
          <p className="text-xs text-gray-500 mb-3">
            © 2026 BORDERLOGIX CORE. ALL RIGHTS RESERVED.
          </p>
          <div className="flex justify-center gap-4 text-xs text-gray-500">
            <a href="https://borderlogixpng.com" target="_blank" rel="noopener noreferrer" className="hover:text-teal-400 transition-colors">Terms of Service</a>
            <span className="text-gray-700">|</span>
            <a href="https://borderlogixpng.com" target="_blank" rel="noopener noreferrer" className="hover:text-teal-400 transition-colors">Privacy Policy</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginView;